import { Box } from '@mui/material';
import { DockviewReact } from 'dockview';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AnalysisPanel from '../components/Analysis/AnalysisPanel';
import AnalysisToolbar from '../components/Analysis/AnalysisToolbar';
import FileBrowserSidebar from '../components/Analysis/FileBrowserSidebar';
import { useAnalysisStore } from '../stores/analysisStore';
import { useProjectStore } from '../stores/projectStore';

/**
 * Dockview panel component — wraps AnalysisPanel with the tabId from panel params.
 */
const AnalysisPanelComponent = ({ params }) => {
  return <AnalysisPanel tabId={params.tabId} />;
};

// Register panel components by id
const PANEL_COMPONENTS = {
  analysisPanel: AnalysisPanelComponent,
};

/**
 * Analysis page — the main analysis workspace.
 * Layout: fixed collapsible sidebar (left) + DockviewReact (right).
 *
 * Files are opened as tabs within dockview panel groups.
 * Each tab corresponds to one open file in analysisStore.
 */
const Analysis = () => {
  const dockviewApiRef = useRef(null);
  const location = useLocation();
  const openFile = useAnalysisStore((state) => state.openFile);
  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);

  // Drives the toolbar's disabled state — kept in sync with dockview's
  // panel collection via add/remove listeners registered in onReady.
  const [panelCount, setPanelCount] = useState(0);

  // Stable accessor for the toolbar so it always reads the latest API.
  const getDockviewApi = useCallback(() => dockviewApiRef.current, []);

  // Handle dockview ready — restore any panels that are already in the store
  const onReady = useCallback((event) => {
    dockviewApiRef.current = event.api;

    const refreshPanelCount = () => setPanelCount(event.api.panels.length);

    // Listen for panel close events to sync with analysisStore
    event.api.onDidRemovePanel((panel) => {
      const tabId = panel.params?.tabId;
      if (tabId) {
        useAnalysisStore.getState().closeFile(tabId);
      }
      refreshPanelCount();
    });

    event.api.onDidAddPanel(() => {
      refreshPanelCount();
    });

    // Listen for active panel change to sync activeTabId
    event.api.onDidActivePanelChange((panel) => {
      if (panel) {
        const tabId = panel.params?.tabId;
        if (tabId) {
          useAnalysisStore.getState().setActiveTab(tabId);
        }
      }
    });

    // Restore panels from analysisStore (survives navigation away and back)
    const { openFiles, activeTabId } = useAnalysisStore.getState();
    Object.entries(openFiles).forEach(([tabId, tab]) => {
      event.api.addPanel({
        id: tabId,
        component: 'analysisPanel',
        title: tab.displayName || tab.filename,
        params: { tabId },
      });
    });
    // Activate the previously active tab
    if (activeTabId) {
      const panel = event.api.panels.find((p) => p.params?.tabId === activeTabId);
      if (panel) panel.api.setActive();
    }
    refreshPanelCount();
  }, []);

  // Open a file as a new dockview tab
  const handleOpenFile = useCallback(
    (projectId, filename) => {
      if (!dockviewApiRef.current) return;

      // openFile is synchronous — registers the tab and starts async data loading
      const tabId = openFile(projectId, filename, filename);
      if (!tabId) return;

      // Check if a panel for this tabId already exists
      const existing = dockviewApiRef.current.panels.find((p) => p.params?.tabId === tabId);
      if (existing) {
        existing.api.setActive();
        return;
      }

      // Add panel immediately — AnalysisPanel shows spinner while data loads
      dockviewApiRef.current.addPanel({
        id: tabId,
        component: 'analysisPanel',
        title: filename,
        params: { tabId },
      });
    },
    [openFile],
  );

  // Handle navigation from ProjectExplorer (via location.state)
  useEffect(() => {
    const state = location.state;
    if (state?.projectId && state?.filename) {
      // Ensure the project is set in the store
      const currentProjectId = useProjectStore.getState().currentProjectId;
      if (currentProjectId !== state.projectId) {
        setCurrentProject(state.projectId);
      }
      // Small delay to let dockview initialize on first render
      const timer = setTimeout(() => {
        handleOpenFile(state.projectId, state.filename);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.state, handleOpenFile, setCurrentProject]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 60px)',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <FileBrowserSidebar onOpenFile={handleOpenFile} />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AnalysisToolbar getApi={getDockviewApi} panelCount={panelCount} />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DockviewReact
            onReady={onReady}
            components={PANEL_COMPONENTS}
            className="dockview-theme-light"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Analysis;
